import { columnWidth as columnWidthOf } from '@sites/entity-core';

import type { DenormalizedColumnEntity } from '@web/types';

import { DestroyRef, Directive, DOCUMENT, ElementRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { fromEvent } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { LayoutFacade } from '@web/store/layout/layout.facade';

/**
 * The box that gets highlighted while a column edge is dragged. A default column
 * outlines its own `.column`; a header one is outlined through the section's
 * `.column-container`, which also carries the width badge. Resolving it with
 * `closest` keeps this independent of how deep the id-bearing element sits.
 */
function resizeOutline(columnId: string): HTMLElement | null {
    return document.getElementById(columnId)?.closest<HTMLElement>('web-column > .column, .column-container') ?? null;
}

/** How long the resized pair keeps its highlight after the button is released. */
const OUTLINE_LINGER_MS = 300;

@Directive({ selector: '[webColumnWidth]' })
export class ColumnWidthDirective {
    private readonly element: HTMLElement = inject(ElementRef).nativeElement;
    private readonly document = inject<Document>(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutFacade = inject(LayoutFacade);

    public readonly columnLeft = input.required<DenormalizedColumnEntity>();
    public readonly columnRight = input.required<DenormalizedColumnEntity>();
    public readonly isColumnWidthFlex = input<boolean>(false);

    public readonly changeWidthToLeftEvent = output<void>();
    public readonly changeWidthToRightEvent = output<void>();

    /**
     * Column widths are stored either as twelfths of the grid or as percentages
     * — `isColumnWidthFlex` picks which — so the drag distance that buys one unit
     * and the floor it may not cross come in matching pairs.
     */
    private readonly DIFF_VALUE: number = 90;
    private readonly MIN_COL_WIDTH: number = 2;
    private readonly DIFF_VALUE_PERCENT: number = 10;
    private readonly MIN_COL_WIDTH_PERCENT: number = 16;

    private isResizing = false;

    constructor() {
        this.initDrag();

        // The handle is dropped from the DOM whenever the column count changes,
        // which can happen mid-gesture. Leaving the store flag on would keep the
        // editor controls hidden and the resize cursor pinned for good.
        this.destroyRef.onDestroy(() => this.stopResizing());
    }

    private initDrag(): void {
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');

        fromEvent<MouseEvent>(this.element, 'mousedown')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((dragStartEvent: MouseEvent) => {
                dragStartEvent.preventDefault();
                this.startResizing();

                let initialX = dragStartEvent.clientX;
                const outlines = [resizeOutline(this.columnLeft().id), resizeOutline(this.columnRight().id)];
                outlines.forEach((outline) => outline?.classList.add('resizing-column'));

                fromEvent<MouseEvent>(this.document, 'mousemove')
                    .pipe(takeUntil(dragEnd$), takeUntilDestroyed(this.destroyRef))
                    .subscribe((dragEvent: MouseEvent) => {
                        dragEvent.preventDefault();

                        const step = this.step;
                        const diff = initialX - dragEvent.clientX;

                        if (Math.abs(diff) < step) {
                            return;
                        }

                        if (diff > step) {
                            if (this.widthOf(this.columnLeft()) > this.minWidth) {
                                this.changeWidthToLeftEvent.emit();
                                initialX -= step;
                            }
                        } else if (diff < step) {
                            if (this.widthOf(this.columnRight()) > this.minWidth) {
                                this.changeWidthToRightEvent.emit();
                                initialX += step;
                            }
                        }
                    });

                dragEnd$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                    this.stopResizing();

                    // The highlight is allowed to linger past the release, unlike
                    // the cursor and the controls, which come back immediately.
                    setTimeout(
                        () => outlines.forEach((outline) => outline?.classList.remove('resizing-column')),
                        OUTLINE_LINGER_MS
                    );
                });
            });
    }

    private get step(): number {
        return this.isColumnWidthFlex() ? this.DIFF_VALUE_PERCENT : this.DIFF_VALUE;
    }

    private get minWidth(): number {
        return this.isColumnWidthFlex() ? this.MIN_COL_WIDTH_PERCENT : this.MIN_COL_WIDTH;
    }

    /** One stored number, in whichever unit the row picked. */
    private widthOf(column: DenormalizedColumnEntity): number {
        return columnWidthOf(column);
    }

    private startResizing(): void {
        this.isResizing = true;
        this.layoutFacade.setColumnWidthChanging(true);
        this.element.classList.add('dragging');
    }

    private stopResizing(): void {
        if (!this.isResizing) {
            return;
        }

        this.isResizing = false;
        this.layoutFacade.setColumnWidthChanging(false);
        this.element.classList.remove('dragging');
    }
}
